import { FilterIcon, Timer01Icon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export interface RoutineStepProps {
  /** 1-based position. */
  index: number;
  title: string;
  description?: string;
  icon: React.ReactNode;
  /** e.g. "WhatsApp", "Llamada", "CRM". */
  channel?: string;
  /** Human delay, e.g. "Inmediato" or "Después de 2 h". */
  delay?: string;
  /** Guard condition, e.g. "Solo si no ha respondido". */
  condition?: string;
  /** Extra status (StatusBadge) or actions on the right. */
  aside?: React.ReactNode;
  /** Draws the connector to the next step. */
  isLast?: boolean;
  className?: string;
}

/** Ordered automation step. Render several inside <RoutineSteps>. */
export const RoutineStep = React.forwardRef<HTMLLIElement, RoutineStepProps>(function RoutineStep(
  { index, title, description, icon, channel, delay, condition, aside, isLast, className },
  ref,
) {
  return (
    <li ref={ref} className={cn('relative flex gap-3 pb-4 last:pb-0', className)}>
      {!isLast ? <span aria-hidden className="absolute left-[19px] top-10 bottom-0 w-px bg-border-subtle" /> : null}
      <span className="relative flex size-10 shrink-0 items-center justify-center rounded-pill border border-border-subtle bg-surface-default text-text-primary [&_svg]:size-[18px]">
        {icon}
      </span>
      <div className="grid min-w-0 flex-1 gap-1 rounded-card border border-border-subtle bg-surface-default px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-body font-semibold text-text-primary">
            <span className="ef-tabular text-text-secondary">Paso {index}. </span>
            {title}
          </p>
          {aside}
        </div>
        {description ? <p className="text-body text-text-secondary">{description}</p> : null}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-meta text-text-secondary [&_svg]:size-3.5">
          {channel ? <span>Canal: {channel}</span> : null}
          {delay ? (
            <span className="inline-flex items-center gap-1">
              <Icon icon={Timer01Icon} aria-hidden />
              {delay}
            </span>
          ) : null}
          {condition ? (
            <span className="inline-flex items-center gap-1">
              <Icon icon={FilterIcon} aria-hidden />
              {condition}
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
});

export function RoutineSteps({ children, label = 'Pasos de la automatización', className }: { children: React.ReactNode; label?: string; className?: string }) {
  return (
    <ol aria-label={label} className={cn('grid', className)}>
      {children}
    </ol>
  );
}
